import SEOHead from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ServiceNavigation from "@/components/ServiceNavigation";
import { motion } from "framer-motion";
import { Video, Check, Sparkles, Target, BarChart3, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import LeadFormModal from "@/components/LeadFormModal";

const features = [
  { text: "Stratégie créative data-driven" },
  { text: "Production multi-formats" },
  { text: "A/B testing créatif" },
  { text: "Optimisation continue" },
  { text: "Reporting performance" },
];

const platforms = [
  { name: "Meta Ads", description: "Facebook & Instagram", icon: Target },
  { name: "YouTube Ads", description: "Pre-roll & In-stream", icon: Video },
  { name: "TikTok Ads", description: "Native & Spark Ads", icon: Zap },
];

const publicitesSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Production de publicités vidéo",
  "serviceType": "Création publicitaire vidéo",
  "description": "Production de publicités vidéo performantes pour Meta, YouTube et TikTok avec stratégie data-driven, A/B testing créatif et optimisation continue.",
  "provider": {
    "@type": "LocalBusiness",
    "name": "Agence 8020",
    "address": { "@type": "PostalAddress", "addressLocality": "Montréal", "addressRegion": "QC", "addressCountry": "CA" },
    "url": "https://agence8020.com"
  },
  "areaServed": { "@type": "Place", "name": "Québec, Canada" }
};

const PublicitesPage = () => {
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEOHead
        title="Publicités vidéo Meta, YouTube & TikTok pour B2B | Agence 8020 Montréal"
        description="Création de publicités vidéo performantes pour Meta, YouTube et TikTok. Stratégie data-driven, A/B testing et optimisation continue pour maximiser votre ROI publicitaire."
        canonical="/publicites"
        schemaJson={publicitesSchema}
      />

      <main className="min-h-screen bg-background overflow-hidden">
        <Navbar />
        
        {/* Hero Section */}
        <section className="pt-32 pb-8 relative">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-gradient-to-b from-primary/20 to-transparent opacity-50 blur-[120px] rounded-full" />
          </div>
          
          <div className="container mx-auto px-6 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
                <Video className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-primary">Performance Marketing</span>
              </div>
              <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight mb-6">
                Publicités <br className="hidden md:block" />
                <span className="text-primary">vidéo</span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Des pubs qui convertissent, pas qui dérangent. Pour Meta, YouTube et TikTok.
              </p>
            </motion.div>
          </div>
        </section>

        <ServiceNavigation />

        {/* Platforms Section */}
        <section className="py-12">
          <div className="container mx-auto px-6">
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {platforms.map((platform, index) => (
                <motion.div
                  key={platform.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="p-6 rounded-2xl bg-card/50 border border-border/50 hover:border-primary/30 hover:bg-card/80 transition-all duration-300 text-center group"
                  whileHover={{ scale: 1.02, y: -4 }}
                >
                  <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-colors">
                    <platform.icon className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-foreground mb-1">{platform.name}</h3>
                  <p className="text-muted-foreground text-sm">{platform.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Video Showcase */}
        <section className="py-12">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto"
            >
              <div className="rounded-2xl bg-card border border-border overflow-hidden hover:border-primary/50 hover-glow transition-all duration-500">
                <div className="relative overflow-hidden" style={{ aspectRatio: "16/9" }}>
                  <iframe
                    src="https://www.youtube.com/embed/_JlqPf3yhKs"
                    title="Publicité vidéo créative"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full"
                  />
                </div>
                <div className="p-4 text-center">
                  <span className="text-primary text-xs font-medium">Exemple de publicité</span>
                  <h3 className="font-heading text-base font-bold text-foreground mt-1">
                    Publicité vidéo créative
                  </h3>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
              >
                Des pubs qui convertissent, pas qui <span className="text-primary">dérangent.</span>
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-xl text-muted-foreground leading-relaxed"
              >
                En plus de la création de contenu organique, nous produisons des publicités vidéo performantes.
                <br />
                <span className="text-foreground font-medium">Pour Meta, YouTube et TikTok - des formats qui génèrent des résultats.</span>
              </motion.p>
            </div>

            {/* Features Grid */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto mb-16"
            >
              {features.map((feature, index) => (
                <motion.div
                  key={feature.text}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-4 p-5 rounded-2xl bg-card/50 border border-border/50 hover:border-primary/30 hover:bg-card/80 transition-all duration-300 group"
                  whileHover={{ scale: 1.02, y: -2 }}
                >
                  <Check className="w-5 h-5 text-primary flex-shrink-0" />
                  <span className="text-foreground font-medium">{feature.text}</span>
                </motion.div>
              ))}
            </motion.div>

            {/* Process Cards */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-16"
            >
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <Target className="w-6 h-6 text-primary" />
                </div>
                <h4 className="font-heading text-lg font-bold mb-2">Stratégie</h4>
                <p className="text-muted-foreground text-sm">Analyse de votre audience et objectifs pour définir les angles créatifs gagnants.</p>
              </div>
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <Video className="w-6 h-6 text-primary" />
                </div>
                <h4 className="font-heading text-lg font-bold mb-2">Production</h4>
                <p className="text-muted-foreground text-sm">Création de multiples variations pour tester et optimiser les performances.</p>
              </div>
              <div className="p-6 rounded-2xl bg-card/50 border border-border/50">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <BarChart3 className="w-6 h-6 text-primary" />
                </div>
                <h4 className="font-heading text-lg font-bold mb-2">Optimisation</h4>
                <p className="text-muted-foreground text-sm">Suivi des métriques et itérations continues pour maximiser votre ROI.</p>
              </div>
            </motion.div>

            {/* Highlight Box */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto"
            >
              <div className="p-8 rounded-3xl bg-primary/10 relative overflow-hidden border border-primary/20">
                <div className="absolute inset-0 bg-background/80" />
                <div className="relative z-10 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-primary flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-6 h-6 text-primary-foreground" />
                  </div>
                  <p className="text-lg text-foreground leading-relaxed">
                    Notre approche allie stratégie, storytelling et optimisation publicitaire, afin de transformer vos vidéos en véritables outils d'acquisition.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 relative">
          <div className="absolute inset-0 bg-gradient-to-t from-primary/5 via-transparent to-transparent" />
          <div className="container mx-auto px-6 relative z-10 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-2xl mx-auto"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Vous voulez des <span className="text-primary">pubs</span> qui performent?
              </h2>
              <p className="text-muted-foreground text-lg mb-8">
                Parlez-nous de votre projet. On vous répond en 24h.
              </p>
              <Button 
                variant="hero" 
                size="xl" 
                className="shadow-neon group"
                onClick={() => setIsLeadModalOpen(true)}
              >
                Parlons de votre projet
                <motion.span animate={{ x: [0, 5, 0] }} transition={{ repeat: Infinity, duration: 1.5 }}>→</motion.span>
              </Button>
            </motion.div>
          </div>
        </section>

        <LeadFormModal isOpen={isLeadModalOpen} onClose={() => setIsLeadModalOpen(false)} />
        <Footer />
      </main>
    </>
  );
};

export default PublicitesPage;